import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {bindActionCreators} from 'redux';
import {FmlxButton, FmlxIcon, FmlxTooltip} from 'fmlx-common-ui';
import {connect} from 'react-redux';
import _ from 'lodash';

import FormattedHTMLMessage from '../FormattedHTMLMessage';
import FastModal from '../FastModal';
import Labware, {WELL_HOVER_MODE} from '../Labware';
import {KeyboardKey, Orientation, TrayType} from '../../constants/dataEnum';
import {showLoading, hideLoading} from '../../stores/actions/appStateAction';
import {getWellStyleColorByLabwareWellCount} from '../../stores/business/labwareBusinessAction';
import withIntl from '../../lib/withIntl';

import './ManualSetTipLocation.scss';
import {setPrevErrorCodeSetTipModal, setSelectedTip, setShowSetTipModal} from '../../stores/actions/tipStateAction';
import {useStaticCallback} from '../../lib/customHooks';
import {setProtocolTip} from '../../stores/business/instrumentBusiness';
import {fastRuntimeErrorResHandling} from '../../stores/business/runtimeBusiness';

const hoverWellColorEnum = '#C0D6FB';
const selectedWellColorEnum = '#0059CC';

function TipCaddy({
  ID,
  colCount,
  rowCount,
  wellStyle,
  onClickWell,
  onSwipeStart,
  onSwipeMove,
  onSwipeEnd,
  onHoverWell,
  onLeaveWell,
  label,
  disabledQuadrant,
}) {
  return (
    <div className="tip-caddy">
      <div className="tip-caddy__wrapper">
        <Labware
          key="manual-set-tip-location"
          ID={`manual-set-tip-location-${ID}`}
          colCount={colCount}
          rowCount={rowCount}
          orientation={Orientation.POTRAIT}
          wellHoverMode={WELL_HOVER_MODE.NOTHING}
          isCustomTips
          swipeEnable
          showSelectionLine={false}
          isWellRectangle={false}
          wellStyle={wellStyle}
          onClickWell={onClickWell}
          onSwipeStart={onSwipeStart}
          onSwipeMove={onSwipeMove}
          onSwipeEnd={onSwipeEnd}
          onHoverWell={onHoverWell}
          onLeaveWell={onLeaveWell}
          disabledQuadrant={disabledQuadrant}
        />
      </div>
      <div className="tip-caddy__label">{label}</div>
    </div>
  );
}

TipCaddy.propTypes = {
  ID: PropTypes.number.isRequired,
  rowCount: PropTypes.number.isRequired,
  colCount: PropTypes.number.isRequired,
  wellStyle: PropTypes.object.isRequired,
  onClickWell: PropTypes.func.isRequired,
  onSwipeStart: PropTypes.func.isRequired,
  onSwipeMove: PropTypes.func.isRequired,
  onSwipeEnd: PropTypes.func.isRequired,
  onHoverWell: PropTypes.func.isRequired,
  onLeaveWell: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  disabledQuadrant: PropTypes.string.isRequired,
};

function ManualSetTipLocation({
  selectedTip,
  showModal,
  prevErrorCode,
  trayType,
  labwareBusinessGetWellStyleColorByLabwareWellCount,
  tipStateActionSetSelectedTip,
  tipStateActionSetShowSetTipModal,
  tipStateActionSetSetPrevErrorCodeSetTipModal,
  instrumentBusinessSetProtocolTip,
  runtimeBusinessFastRuntimeErrorResHandling,
  onShowLoading,
  onHideLoading,
  intl,
}) {
  const [selectedWellStyleMap, setSelectedWellStyleMap] = useState({});
  const swipeInfoRef = useRef(null);
  const hoveredWellRef = useRef(null);
  const selectedTipRef = useRef(selectedTip);

  useEffect(() => {
    selectedTipRef.current = selectedTip;
  }, [selectedTip]);

  const getTipCaddy = () => {
    switch (trayType) {
      case TrayType.NORMAL_CADDY:
        return [{ID: 1, label: 'Caddy 1', colCount: 24, rowCount: 16, disabledQuadrant: []}];
      case TrayType.HIGH_VOLUME_CADDY:
        return [{ID: 1, label: 'Caddy 1', colCount: 24, rowCount: 16, disabledQuadrant: [2, 4]}];
      case TrayType.DOUBLE_CADDY:
        return [
          {ID: 1, label: 'Caddy 1', colCount: 24, rowCount: 16, disabledQuadrant: []},
          {ID: 2, label: 'Caddy 2', colCount: 24, rowCount: 16, disabledQuadrant: []},
        ];
      default:
        return [];
    }
  };

  const handleClearAll = () => {
    tipStateActionSetSelectedTip({1: [], 2: []});
  };

  const generateWellStyle = (ID) => {
    const wellStyle = {};

    const caddySelectedTip = selectedTip[ID];
    for (let iTip = 0; iTip < caddySelectedTip.length; iTip += 1) {
      const {col, row} = caddySelectedTip[iTip];
      wellStyle[`${parseInt(row, 10) + 1}-${parseInt(col, 10) + 1}-0`] = {
        background: selectedWellColorEnum,
      };
    }

    return wellStyle;
  };

  const updateWellStyle = (ID, isShift) => {
    const isMoving = swipeInfoRef.current;

    const selectedWellMap = {};
    const hoveredWellMap = {};

    if (swipeInfoRef.current) {
      const {startingRow, startingColumn, rowTipCount, columnTipCount} = swipeInfoRef.current;

      selectedWellMap[ID] = labwareBusinessGetWellStyleColorByLabwareWellCount(
        16,
        24,
        startingRow,
        startingColumn,
        hoverWellColorEnum,
        rowTipCount,
        columnTipCount,
        null,
        false,
        null,
        null,
        isShift ? 0 : 1,
        isShift ? 0 : 1,
      );
    }

    if (hoveredWellRef.current && !isMoving) {
      const {row: hoveredRow, column: hoveredColumn} = hoveredWellRef.current;

      hoveredWellMap[ID] = labwareBusinessGetWellStyleColorByLabwareWellCount(
        8,
        12,
        hoveredRow,
        hoveredColumn,
        hoverWellColorEnum,
        1,
        1,
        null,
        false,
        null,
        null,
        0,
        0,
      );

      Object.keys(hoveredWellMap).forEach((e) => {
        selectedWellMap[e] = hoveredWellMap[e];
      });
    }

    setSelectedWellStyleMap(selectedWellMap);
  };

  const handleHoverWell = (ID, wellID) => {
    if (swipeInfoRef.current) {
      return;
    }

    const [row, column] = wellID.split('-');
    hoveredWellRef.current = {row: Number(row), column: Number(column)};

    updateWellStyle(ID);
  };

  const handleSwipeStart = (ID, wellID, isShift) => {
    const [row, column] = wellID.split('-');
    swipeInfoRef.current = {ID, actualStartingRow: Number(row), actualStartingColumn: Number(column), isShift};
  };

  const handleSwipeMove = (ID, wellID, isShift) => {
    if (!swipeInfoRef.current) {
      return;
    }

    const [row, column] = wellID.split('-');
    const {actualStartingRow, actualStartingColumn, ID: caddyID} = swipeInfoRef.current;

    if (caddyID !== ID) {
      return;
    }

    let startingRow = Math.min(actualStartingRow, row);
    let startingColumn = Math.min(actualStartingColumn, column);

    // normalize startingRow if swiping to the right
    if (startingRow < actualStartingRow && !isShift) {
      startingRow += actualStartingRow % 2 !== startingRow % 2 ? 1 : 0;
    }
    // normalize startingColumn if swiping to the up
    if (startingColumn < actualStartingColumn && !isShift) {
      startingColumn += actualStartingColumn % 2 !== startingColumn % 2 ? 1 : 0;
    }

    const endRow = Math.max(actualStartingRow, row);
    const endColumn = Math.max(actualStartingColumn, column);

    const rowTipCount = Math.abs(actualStartingRow - row) + 1;
    const columnTipCount = Math.abs(actualStartingColumn - column) + 1;

    // normalize tip count regarding shift button
    const normalizeRowTipCount = isShift ? rowTipCount : Math.ceil(rowTipCount / 2);
    const normalizeColumnTipCount = isShift ? columnTipCount : Math.ceil(columnTipCount / 2);

    swipeInfoRef.current = {
      ...swipeInfoRef.current,
      startingRow,
      startingColumn,
      endRow,
      endColumn,
      rowTipCount: normalizeRowTipCount,
      columnTipCount: normalizeColumnTipCount,
      isShift,
    };

    updateWellStyle(ID, isShift);
  };

  const handleSwipeEnd = (ID, isShift) => {
    if (!swipeInfoRef.current) {
      return;
    }

    const {
      startingRow,
      startingColumn,
      rowTipCount,
      columnTipCount,
      ID: caddyID,
      isShift: isShiftInfo,
    } = swipeInfoRef.current;

    const caddySelectedTip = selectedTipRef.current[caddyID];

    const toBeAdded = [];
    const toBeDeleted = [];

    const skipRow = !(isShiftInfo || isShift);
    const skipColumn = !(isShiftInfo || isShift);

    for (let iRow = 0; iRow < rowTipCount; iRow += 1) {
      for (let iCol = 0; iCol < columnTipCount; iCol += 1) {
        const newRow = startingRow + (skipRow ? iRow * 2 : iRow) - 1;
        const newColumn = startingColumn + (skipColumn ? iCol * 2 : iCol) - 1;

        if (
          trayType === TrayType.HIGH_VOLUME_CADDY &&
          (((newRow + 1) % 2 === 1 && (newColumn + 1) % 2 === 0) ||
            ((newRow + 1) % 2 === 0 && (newColumn + 1) % 2 === 1))
        ) {
          continue;
        }

        const isSelected = caddySelectedTip.find((tip) => tip.col === newColumn && tip.row === newRow);

        if (isSelected) {
          toBeDeleted.push({
            col: newColumn,
            row: newRow,
          });
        } else {
          toBeAdded.push({
            col: newColumn,
            row: newRow,
          });
        }
      }
    }
    const diffrentTip = _.differenceWith(caddySelectedTip, toBeDeleted, _.isEqual);

    tipStateActionSetSelectedTip({...selectedTip, [caddyID]: [...toBeAdded, ...diffrentTip]});
    swipeInfoRef.current = null;
    hoveredWellRef.current = null;
    updateWellStyle(caddyID, isShift);
  };

  const handleClickWell = (ID, wellID) => {
    handleSwipeStart(ID, wellID);

    setTimeout(() => {
      handleSwipeMove(ID, wellID);

      setTimeout(() => {
        handleSwipeEnd(ID);
      }, 1);
    }, 1);
  };

  const handleLeaveWell = () => {
    if (!swipeInfoRef.current) {
      setSelectedWellStyleMap({});
    }
  };

  const onKeyRelease = useStaticCallback((evt) => {
    if (evt.keyCode === KeyboardKey.SHIFT && swipeInfoRef.current) {
      const {isShift, startingRow, startingColumn, endRow, endColumn, ID} = swipeInfoRef.current;

      if (isShift) {
        const rowTipCount = Math.ceil((Math.abs(startingRow - endRow) + 1) / 2);
        const columnTipCount = Math.ceil((Math.abs(startingColumn - endColumn) + 1) / 2);
        swipeInfoRef.current = {
          ...swipeInfoRef.current,
          rowTipCount,
          columnTipCount,
          isShift: false,
        };
      }

      updateWellStyle(ID, false);
    }
  });

  const onKeyPress = useStaticCallback((evt) => {
    if (evt.keyCode === KeyboardKey.SHIFT && swipeInfoRef.current) {
      const {isShift, startingRow, startingColumn, endRow, endColumn, ID} = swipeInfoRef.current;

      if (!isShift) {
        const rowTipCount = Math.abs(startingRow - endRow) + 1;
        const columnTipCount = Math.abs(startingColumn - endColumn) + 1;
        swipeInfoRef.current = {
          ...swipeInfoRef.current,
          rowTipCount,
          columnTipCount,
          isShift: true,
        };
      }
      updateWellStyle(ID, true);
    }
  });

  useEffect(() => {
    document.addEventListener('keyup', onKeyRelease);
    document.addEventListener('keydown', onKeyPress);

    return () => {
      document.removeEventListener('keyup', onKeyRelease);
      document.removeEventListener('keydown', onKeyPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const button = [
    {
      className: 'btn-lg',
      style: 'primary',
      handlerClick: async () => {
        onShowLoading();
        if (trayType === TrayType.DOUBLE_CADDY) {
          await instrumentBusinessSetProtocolTip(Object.values(selectedTipRef.current), [1, 2]);
        } else {
          await instrumentBusinessSetProtocolTip([selectedTipRef.current[1]], [2]);
        }
        tipStateActionSetShowSetTipModal(false);
        handleClearAll();
        onHideLoading();
      },
      text: intl.formatMessage({id: 'modal.manualSetTipLocation.firstButton'}),
    },
    {
      className: 'btn-lg',
      style: 'secondary',
      handlerClick: () => {
        tipStateActionSetShowSetTipModal(false);
        runtimeBusinessFastRuntimeErrorResHandling(prevErrorCode);
        tipStateActionSetSetPrevErrorCodeSetTipModal(null);
        handleClearAll();
      },
      text: intl.formatMessage({id: 'modal.manualSetTipLocation.secondButton'}),
    },
  ];

  return (
    <FastModal
      show={showModal}
      btnAction={button}
      header={intl.formatMessage({id: 'modal.manualSetTipLocation.title'})}
      size="lg"
      closeDisabled
      content={
        <div className="manual-set-tip-location">
          <span className="manual-set-tip-location__text">
            <FormattedHTMLMessage id="modal.manualSetTipLocation.text" />
          </span>
          <div className="manual-set-tip-location__tip-caddy-wrapper">
            {getTipCaddy().map((tipCaddy) => (
              <TipCaddy
                ID={tipCaddy.ID}
                colCount={tipCaddy.colCount}
                rowCount={tipCaddy.rowCount}
                wellStyle={{...generateWellStyle(tipCaddy.ID), ...selectedWellStyleMap[tipCaddy.ID]}}
                onClickWell={(wellID) => handleClickWell(tipCaddy.ID, wellID)}
                onSwipeStart={(wellID, isCtrl, isShift) => handleSwipeStart(tipCaddy.ID, wellID, isShift)}
                onSwipeMove={(wellID, swipingDirectionY, swipingDirectionX, isCtrl, isShift) => {
                  handleSwipeMove(tipCaddy.ID, wellID, isShift);
                }}
                onSwipeEnd={(wellID, startWellId, isCtrl, isShift) => handleSwipeEnd(tipCaddy.ID, isShift)}
                onHoverWell={(wellID) => handleHoverWell(tipCaddy.ID, wellID)}
                onLeaveWell={handleLeaveWell}
                label={tipCaddy.label}
                disabledQuadrant={tipCaddy.disabledQuadrant}
              />
            ))}
          </div>
          <div className="manual-set-tip-location__label">
            <span>Front</span>
            <FmlxTooltip
              title={<img src={`./images/manual-set-tip-location/front-${trayType}.png`} width={240} alt="" />}
              placement="top"
            >
              <span>
                <FmlxIcon name="InfoOutline" size="xs" />
              </span>
            </FmlxTooltip>
          </div>
          <div className="manual-set-tip-location__clear-button">
            <FmlxButton
              label="CLEAR ALL"
              variant="plain"
              withIcon="start"
              icon={<FmlxIcon name="Trash" size="sm" />}
              onClick={handleClearAll}
            />
          </div>
        </div>
      }
      onClose={() => tipStateActionSetShowSetTipModal(false)}
    />
  );
}

function mapStateToProps(state) {
  return {
    selectedTip: state.tipState.selectedTip,
    showModal: state.tipState.showModal,
    prevErrorCode: state.tipState.prevErrorCode,
    trayType: state.trayCalibrationState.trayType,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onShowLoading: bindActionCreators(showLoading, dispatch),
    onHideLoading: bindActionCreators(hideLoading, dispatch),
    labwareBusinessGetWellStyleColorByLabwareWellCount: bindActionCreators(
      getWellStyleColorByLabwareWellCount,
      dispatch,
    ),
    tipStateActionSetSelectedTip: bindActionCreators(setSelectedTip, dispatch),
    tipStateActionSetShowSetTipModal: bindActionCreators(setShowSetTipModal, dispatch),
    tipStateActionSetSetPrevErrorCodeSetTipModal: bindActionCreators(setPrevErrorCodeSetTipModal, dispatch),
    instrumentBusinessSetProtocolTip: bindActionCreators(setProtocolTip, dispatch),
    runtimeBusinessFastRuntimeErrorResHandling: bindActionCreators(fastRuntimeErrorResHandling, dispatch),
  };
}

ManualSetTipLocation.propTypes = {
  selectedTip: PropTypes.object.isRequired,
  showModal: PropTypes.bool.isRequired,
  prevErrorCode: PropTypes.bool.isRequired,
  trayType: PropTypes.object.isRequired,
  labwareBusinessGetWellStyleColorByLabwareWellCount: PropTypes.func.isRequired,
  tipStateActionSetSelectedTip: PropTypes.func.isRequired,
  tipStateActionSetShowSetTipModal: PropTypes.func.isRequired,
  tipStateActionSetSetPrevErrorCodeSetTipModal: PropTypes.func.isRequired,
  instrumentBusinessSetProtocolTip: PropTypes.func.isRequired,
  runtimeBusinessFastRuntimeErrorResHandling: PropTypes.func.isRequired,
  onShowLoading: PropTypes.func.isRequired,
  onHideLoading: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(ManualSetTipLocation));
